<template>
  <validation-observer ref="observer">
    <b-form
      @submit.prevent="submit"
    >
      <b-row>
        <b-col cols="12">
          <b-form-group
            :label="$t('fields.title')"
            label-for="subtask-title"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('fields.title')"
              rules="required|min:2"
            >
              <b-form-input
                id="subtask-title"
                v-model="form.title"
                :disabled="readOnly"
                :placeholder="$t('subtasks.form.titlePlaceholder')"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group
            :label="$t('fields.description')"
            label-for="subtask-desc"
          >
            <b-form-textarea
              id="subtask-desc"
              v-model="form.description"
              :disabled="readOnly"
              rows="2"
              :placeholder="$t('subtasks.form.descriptionPlaceholder')"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group
            :label="$t('subtasks.form.assignees')"
          >
            <v-select
              v-model="form.assignees"
              :options="usersList"
              label="name"
              :reduce="item => item.id"
              :placeholder="$t('subtasks.form.selectAssignee')"
              multiple
              :disabled="readOnly"
            />
          </b-form-group>
        </b-col>
        <template v-if="hasDates">
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group :label="$t('fields.startDate')">
              <b-input-group>
                <b-form-datepicker
                  v-model="form.start_date"
                  v-bind="datepickerProps"
                  :placeholder="$t('fields.selectDate')"
                  :disabled="readOnly"
                  :min="minDate"
                  :max="maxDate"
                />
                <template #append>
                  <b-button
                    variant="warning"
                    @click="form.start_date = null"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </template>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group :label="$t('subtasks.form.targetDate')">
              <b-input-group>
                <b-form-datepicker
                  v-model="form.target_date"
                  v-bind="datepickerProps"
                  :placeholder="$t('fields.selectDate')"
                  :disabled="readOnly"
                  :min="minDate"
                  :max="maxDate"
                />
                <template #append>
                  <b-button
                    variant="warning"
                    @click="form.target_date = null"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </template>
              </b-input-group>
            </b-form-group>
          </b-col>
        </template>
        <b-col cols="12">
          <b-form-group
            :label="$t('subtasks.form.closeStatus')"
            label-for="close_status"
          >
            <v-select
              id="close_status"
              v-model="form.id_subtask_statuses"
              :options="subtaskStatuses"
              label="title"
              :reduce="item => item.id"
              :clearable="false"
              :disabled="readOnly"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="resultNotesRequired"
          cols="12"
        >
          <b-form-group
            :label="$t('subtasks.form.resultNotes')"
            label-for="result_notes"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('subtasks.form.resultNotes')"
              :rules="resultNotesRequired ? 'required' : ''"
            >
              <b-form-textarea
                id="result_notes"
                v-model="form.result_notes"
                :disabled="readOnly"
                rows="3"
                :placeholder="$t('subtasks.form.resultNotesPlaceholder')"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <b-col
          v-if="hasTimerOptions"
          cols="12"
        >
          <b-form-group
            :label="$t('subtasks.form.autoStop')"
          >
            <b-form-input
              v-model.number="form.timer_auto_stop_minutes"
              type="number"
              min="0"
              :disabled="readOnly"
              :placeholder="$t('subtasks.form.autoStopPlaceholder')"
            />
            <b-form-text>
              {{ $t('subtasks.form.autoStopHelp') }}
            </b-form-text>
          </b-form-group>
        </b-col>
      </b-row>
      <div class="d-flex justify-content-between mt-1 pt-1 border-top">
        <b-button
          variant="warning"
          @click="$emit('cancelled')"
        >
          {{ $t('common.cancel') }}
        </b-button>
        <b-button
          v-if="!readOnly"
          variant="primary"
          type="submit"
        >
          {{ $t('common.save') }}
        </b-button>
      </div>
    </b-form>
  </validation-observer>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BForm, BFormGroup, BFormInput, BFormText, BFormTextarea, BFormDatepicker, BRow, BCol, BButton, BInputGroup,
} from 'bootstrap-vue'
import { required } from '@validations'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { definitionLabel, definitionId, statusSlug } from '@/libs/definitions'
import { datepickerLocale } from '@/libs/i18n/datepicker'

export default {
  name: 'SubtaskForm',
  components: {
    ValidationObserver,
    ValidationProvider,
    BForm,
    BFormGroup,
    BFormInput,
    BFormText,
    BFormTextarea,
    BFormDatepicker,
    BRow,
    BCol,
    BButton,
    vSelect,
    BInputGroup,
  },
  mixins: [datepickerLocale],
  props: {
    idTasks: {
      type: Number,
      required: true,
    },
    initialData: {
      type: Object,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    hasDates: {
      type: Boolean,
      default: false,
    },
    hasTimerOptions: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      required,
      form: {
        id: null,
        title: '',
        description: '',
        assignees: [],
        start_date: null,
        target_date: null,
        id_subtask_statuses: definitionId('subtask', 'open'),
        id_subtask_priorities: 1,
        result_notes: null,
        timer_auto_stop_minutes: 0,
      },

    }
  },
  computed: {
    usersList() {
      return this.$store.getters['users/dataList'] || []
    },
    subtaskStatuses() {
      return (this.$store.getters['subtaskStatuses/dataList'] || [])
        .map(item => ({ ...item, title: definitionLabel('subtask', item.slug, item.title) }))
    },
    resultNotesRequired() {
      return statusSlug('subtask', this.form) !== 'open'
    },
    parentTask() {
      return this.$store.getters['tasks/dataItem'] || {}
    },
    minDate() {
      const start = this.parentTask.start_date
        ? this.moment(this.parentTask.start_date).format('YYYY-MM-DD') : null
      if (this.form.id) return start
      const today = this.moment().format('YYYY-MM-DD')
      if (!start) return today
      return start > today ? start : today
    },
    maxDate() {
      return this.parentTask.target_date ? this.moment(this.parentTask.target_date).format('YYYY-MM-DD') : null
    },
  },
  watch: {
    initialData: {
      handler(val) {
        if (val) this.fillForm(val)
        else this.resetForm()
      },
      immediate: true,
    },
  },
  created() {
    this.loadUsers()
    this.loadStatuses()
  },
  methods: {
    loadStatuses() {
      this.$store.dispatch('subtaskStatuses/getDataList', {
        select: ['subtask_statuses.id AS id', 'subtask_statuses.title AS title', 'subtask_statuses.slug AS slug'],
      })
    },
    loadUsers() {
      this.$store.dispatch('users/getDataList', {
        select: ['users.id AS id', 'users.name AS name'],
        where: { 'users.status': 1 },
        limit: 500,
        start: 0,
      })
    },
    async fillForm(data) {
      let assigneeIds = []
      if (data.assignees && Array.isArray(data.assignees)) {
        assigneeIds = data.assignees.map(a => (typeof a === 'object' ? a.id : a))
      } else if (data.id) {
        const list = await this.fetchAssignees(data.id)
        assigneeIds = (list || []).map(a => a.id_assigned_users || a.id).filter(Boolean)
      }
      this.form = {
        id: data.id || null,
        title: data.title || '',
        description: data.description || '',
        assignees: assigneeIds,
        start_date: data.start_date || null,
        target_date: data.target_date ? this.moment(data.target_date).format('YYYY-MM-DD') : null,
        id_subtask_statuses: data.id_subtask_statuses || definitionId('subtask', 'open'),
        result_notes: data.result_notes || null,
        timer_auto_stop_minutes: data.timer_auto_stop_minutes ?? 0,
      }
    },
    async fetchAssignees(idSubtasks) {
      const list = await this.$store.dispatch('subtaskAssignees/getDataList', {
        where: { 'subtask_assignees.id_subtasks': idSubtasks },
        limit: 100,
        start: 0,
      })
      return list || []
    },
    resetForm() {
      this.form = {
        id: null,
        title: '',
        description: '',
        assignees: [],
        start_date: null,
        target_date: null,
        id_subtask_statuses: definitionId('subtask', 'open'),
        result_notes: null,
        timer_auto_stop_minutes: 0,
      }
    },
    reset() {
      if (this.initialData) this.fillForm(this.initialData)
      else this.resetForm()
      // eslint-disable-next-line no-unused-expressions
      this.$refs.observer?.reset()
    },
    async submit() {
      const valid = await this.$refs.observer.validate()
      if (!valid) return

      const payload = {
        id_tasks: this.idTasks,
        title: this.form.title.trim(),
        description: this.form.description?.trim() || null,
        assignees: this.form.assignees,
        start_date: this.form.start_date || null,
        target_date: this.form.target_date || null,
        id_subtask_statuses: this.form.id_subtask_statuses,
        result_notes: this.resultNotesRequired ? (this.form.result_notes?.trim() || null) : null,
        timer_auto_stop_minutes: this.hasTimerOptions ? (this.form.timer_auto_stop_minutes || 0) : 0,
      }
      if (this.form.id) payload.id = this.form.id

      try {
        const res = await this.$store.dispatch('subtasks/saveData', payload)
        if (res?.status !== false) {
          this.$emit('saved')
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              variant: 'danger', title: this.$t('common.toast.errorShort'), icon: 'XIcon', text: res?.message || this.$t('subtasks.form.saveFailed'),
            },
          })
        }
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            variant: 'danger', title: this.$t('common.toast.errorShort'), icon: 'XIcon', text: e?.message || this.$t('subtasks.form.saveFailed'),
          },
        })
      }
    },
  },
}
</script>
